"use client"

import Image from "next/image"
import { useEffect, useState, type AnimationEvent } from "react"
import { TranslatedContent } from '@/shared/i18n/provider'

const MOTION_END_MS = 1_720
const FALLBACK_BUFFER_MS = 80

/**
 * 첫 문서 진입 때 한 번만 나타나는 로딩 오버레이.
 * Figma 6294:3485의 브랜드 스플래시와 피드백 모션을 CSS로 옮겼다.
 * RootLayout은 클라이언트 라우팅 중 유지되므로 페이지 내부 이동에는 다시 재생되지 않는다.
 */
export function InitialLoader() {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (!visible) return

    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
    const previousOverflow = document.documentElement.style.overflow

    if (!reducedMotion) {
      document.documentElement.style.overflow = "hidden"
    }

    const fallbackTimer = window.setTimeout(
      () => setVisible(false),
      reducedMotion ? 0 : MOTION_END_MS + FALLBACK_BUFFER_MS,
    )

    return () => {
      window.clearTimeout(fallbackTimer)
      document.documentElement.style.overflow = previousOverflow
    }
  }, [visible])

  if (!visible) return null

  const finishLoading = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      setVisible(false)
    }
  }

  return (
    <TranslatedContent><div
      className="initial-loader"
      data-node-id="6294:3485"
      data-testid="initial-loader"
      role="status"
      aria-label="시선 시작 화면"
      onAnimationEnd={finishLoading}
    >
      <div className="initial-loader__brand-position" aria-hidden="true">
        <div className="brand-logo" data-node-id="6294:3505" data-testid="brand-logo">
          <Image
            className="brand-logo__wordmark"
            src="/figma/app-bar/logo.svg"
            alt=""
            width={78}
            height={23}
            priority
          />
          <span className="brand-logo__tagline">시술 선택의 기준</span>
        </div>
      </div>
      <p className="initial-loader__disclaimer">시선은 시술을 추천하지 않습니다</p>
    </div></TranslatedContent>
  )
}
